import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import type { ClientsDetailedListResponse } from '@common/types'
import { api } from '@frontend/lib/api'
import { AppLayout } from '@frontend/components/layout/AppLayout'
import { Button } from '@frontend/components/ui/button'
import { Card, CardContent } from '@frontend/components/ui/card'
import { Spinner } from '@frontend/components/ui/spinner'
import { ClientsTable, AddClientModal } from '@frontend/components/clients'

async function fetchClients(): Promise<ClientsDetailedListResponse> {
  return api.get('clients').json<ClientsDetailedListResponse>()
}

export function Clients() {
  const [addClientOpen, setAddClientOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['clients-detailed'],
    queryFn: fetchClients,
  })

  const clients = data?.clients ?? []

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Client Management</h1>
            <p className="mt-1 text-muted-foreground">Manage client contracts and Drata API keys</p>
          </div>
          <Button onClick={() => setAddClientOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Client
          </Button>
        </div>

        {/* Main content card */}
        <Card className="shadow-sm">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex justify-center py-16">
                <Spinner size="lg" />
              </div>
            ) : error ? (
              <div className="py-16 text-center">
                <p className="text-destructive">Failed to load clients.</p>
                <p className="mt-1 text-sm text-muted-foreground">Please try refreshing the page.</p>
              </div>
            ) : (
              <div className="p-4">
                <ClientsTable clients={clients} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Client Modal */}
      <AddClientModal open={addClientOpen} onOpenChange={setAddClientOpen} />
    </AppLayout>
  )
}
